"use client";

import { useEffect, useId, useState } from "react";

import type { BookCallPopupVariant } from "@/lib/book-call-popup-copy";
import type { CampaignContext } from "@/lib/campaign-tracking";
import type { Lang } from "@/lib/types";

import { CcCampaignBooking } from "./CcCampaignBooking";
import { CcCampaignForm } from "./CcCampaignForm";
import { CAMPAIGN_WRITE_HASH } from "./campaign-anchors";
import type { CampaignFinalCtaCopy } from "./campaign-landing-page-copy";

type CcCampaignContactProps = {
  lang: Lang;
  copy: CampaignFinalCtaCopy;
  privacyHref: string;
  bookingVariant: BookCallPopupVariant;
  tracking: CampaignContext;
};

/**
 * The closing band's two ways in: the embedded calendar (default) or the
 * short message form. Both panels stay mounted once opened so a half-filled
 * form survives a switch; `#skriv` opens the form directly.
 */
export function CcCampaignContact({ lang, copy, privacyHref, bookingVariant, tracking }: CcCampaignContactProps) {
  const id = useId();
  const [tab, setTab] = useState<"book" | "write">("book");
  const [formOpened, setFormOpened] = useState(false);

  useEffect(() => {
    const sync = () => {
      if (window.location.hash === `#${CAMPAIGN_WRITE_HASH}`) {
        setTab("write");
        setFormOpened(true);
      }
    };
    sync();
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, []);

  const select = (next: "book" | "write") => {
    setTab(next);
    if (next === "write") setFormOpened(true);
    if (next === "book" && window.location.hash === `#${CAMPAIGN_WRITE_HASH}`) {
      // Let the hero link trigger hashchange again after returning to booking.
      window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
    }
  };

  return (
    <div className="cc-campaign-contact" id={CAMPAIGN_WRITE_HASH}>
      <div className="cc-campaign-contact__tabs" role="tablist" aria-label={copy.tabsLabel}>
        {(["book", "write"] as const).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            id={`${id}-${key}-tab`}
            aria-selected={tab === key}
            aria-controls={`${id}-${key}`}
            className="cc-campaign-contact__tab"
            onClick={() => select(key)}
          >
            {copy.tabs[key]}
          </button>
        ))}
      </div>
      <div
        id={`${id}-book`}
        role="tabpanel"
        aria-labelledby={`${id}-book-tab`}
        hidden={tab !== "book"}
        className="cc-campaign-contact__panel"
      >
        <CcCampaignBooking lang={lang} bookingVariant={bookingVariant} />
      </div>
      <div
        id={`${id}-write`}
        role="tabpanel"
        aria-labelledby={`${id}-write-tab`}
        hidden={tab !== "write"}
        className="cc-campaign-contact__panel cc-campaign-contact__panel--form"
      >
        {formOpened ? <CcCampaignForm lang={lang} copy={copy.form} privacyHref={privacyHref} tracking={tracking} /> : null}
      </div>
    </div>
  );
}
